import { zodResolver } from "@hookform/resolvers/zod";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import isNil from "lodash/isNil";
import { useEffect, useState } from "react";
import {
  type Control,
  Controller,
  type Path,
  useForm,
  useWatch,
} from "react-hook-form";

import useQueryClient from "../../../api/query";
import { stall } from "../../../utils/utils";
import { ErrorHelperText } from "../../PatientsDashboard/ui/ErrorHelper";
import { MASK } from "../../redact";
import {
  canonicalizeTarget,
  OPS_CATEGORY,
  OPS_CATEGORY_LABELS,
  OPS_MFA_TYPE,
  OPS_MFA_TYPE_LABELS,
  OPS_OWNER,
  OPS_OWNER_LABELS,
  OPS_STATUS,
  OPS_STATUS_LABELS,
} from "../common";
import { InputLabelText } from "../ui/InputLabelText";
import { InputWrapper } from "../ui/InputWrapper";
import { LineBreak } from "../ui/LineBreak";
import { AuthenticatorSetupKeyField } from "./AuthenticatorSetupKeyField";
import {
  type OpsCredentialPayload,
  type OpsCredentialsFormFields,
  opsCredentialsFormSchema,
} from "./OpsCredentialsForm.zod";

const rowSx = {
  width: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
} as const;

// Taller rows (authenticator input, security-questions section) keep the label
// aligned to the top of the content.
const sectionSx = {
  ...rowSx,
  alignItems: "flex-start",
} as const;

const inputSx = {
  backgroundColor: "#F9FAFB",
  width: "100%",
  fontSize: "1rem",
  ".MuiInputBase-root": {
    borderRadius: "8px",
    border: "none",
  },
  ".MuiOutlinedInput-notchedOutline": {
    border: "solid 1px rgba(208, 213, 221, 0.5)",
  },
} as const;

const selectSx = {
  backgroundColor: "#F9FAFB",
  width: "100%",
  borderRadius: "8px",
  fontSize: "0.875rem",
  ".MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
    {
      padding: "12px 14px",
    },
  ".MuiOutlinedInput-notchedOutline": {
    border: "solid 1px rgba(208, 213, 221, 0.5)",
  },
} as const;

const addButtonSx = {
  textTransform: "none",
  color: "#25233F",
  fontWeight: 600,
  fontSize: "0.875rem",
  padding: 0,
  minWidth: 0,
  justifyContent: "flex-start",
  alignSelf: "flex-start",
  "&:hover": { backgroundColor: "transparent" },
  // padding is zeroed above, so the default -4px would clip the icon
  "& .MuiButton-startIcon": { marginLeft: 0 },
} as const;

interface TextRowProps {
  control: Control<OpsCredentialsFormFields>;
  name: Path<OpsCredentialsFormFields>;
  label: string;
  placeholder?: string;
  type?: string;
  multiline?: boolean;
  rows?: number;
}

function TextRow({
  control,
  name,
  label,
  placeholder,
  type = "text",
  multiline = false,
  rows = 4,
}: TextRowProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onBlur, onChange },
        fieldState: { error },
      }) => (
        <FormControl sx={rowSx}>
          <FormLabel>
            <InputLabelText>{label}</InputLabelText>
          </FormLabel>
          <InputWrapper>
            <TextField
              name={name}
              value={value ?? ""}
              type={type}
              variant="outlined"
              placeholder={placeholder}
              multiline={multiline}
              rows={multiline ? rows : undefined}
              ref={ref}
              onChange={onChange}
              onBlur={onBlur}
              inputProps={{ autoComplete: "off" }}
              sx={inputSx}
            />
            <ErrorHelperText message={error?.message} />
          </InputWrapper>
        </FormControl>
      )}
    />
  );
}

TextRow.defaultProps = {
  placeholder: undefined,
  type: "text",
  multiline: false,
  rows: 4,
};

interface BareTextFieldProps {
  control: Control<OpsCredentialsFormFields>;
  name: Path<OpsCredentialsFormFields>;
  placeholder?: string;
}

// Text field with no label column — used inside the security-questions section.
function BareTextField({ control, name, placeholder }: BareTextFieldProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onBlur, onChange },
        fieldState: { error },
      }) => (
        <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <TextField
            name={name}
            value={value ?? ""}
            variant="outlined"
            placeholder={placeholder}
            ref={ref}
            onChange={onChange}
            onBlur={onBlur}
            inputProps={{ autoComplete: "off" }}
            sx={inputSx}
          />
          <ErrorHelperText message={error?.message} />
        </Box>
      )}
    />
  );
}

BareTextField.defaultProps = {
  placeholder: undefined,
};

interface SelectRowProps {
  control: Control<OpsCredentialsFormFields>;
  name: Path<OpsCredentialsFormFields>;
  label: string;
  options: { value: string; label: string }[];
  disabled?: boolean;
}

function SelectRow({
  control,
  name,
  label,
  options,
  disabled = false,
}: SelectRowProps) {
  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { ref, value, onBlur, onChange },
        fieldState: { error },
      }) => (
        <FormControl sx={rowSx}>
          <FormLabel>
            <InputLabelText>{label}</InputLabelText>
          </FormLabel>
          <InputWrapper>
            <Select
              name={name}
              value={value ?? ""}
              ref={ref}
              onChange={onChange}
              onBlur={onBlur}
              disabled={disabled}
              displayEmpty
              IconComponent={KeyboardArrowDownOutlinedIcon}
              MenuProps={{ sx: { marginTop: "1rem" } }}
              sx={selectSx}
              renderValue={(current) => {
                const match = options.find((o) => o.value === current);
                return match ? match.label : `Choose ${label}`;
              }}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <ErrorHelperText message={error?.message} />
          </InputWrapper>
        </FormControl>
      )}
    />
  );
}

SelectRow.defaultProps = {
  disabled: false,
};

const CATEGORY_OPTIONS = Object.values(OPS_CATEGORY).map((value) => ({
  value,
  label: OPS_CATEGORY_LABELS[value],
}));

// Leading empty choice lets the user clear the owner back to none.
const OWNER_OPTIONS = [
  { value: "", label: "None" },
  ...Object.values(OPS_OWNER).map((value) => ({
    value,
    label: OPS_OWNER_LABELS[value],
  })),
];

const STATUS_OPTIONS = Object.values(OPS_STATUS).map((value) => ({
  value,
  label: OPS_STATUS_LABELS[value],
}));

const MFA_TYPE_OPTIONS = Object.values(OPS_MFA_TYPE).map((value) => ({
  value,
  label: OPS_MFA_TYPE_LABELS[value],
}));

const MAX_SECURITY_QUESTIONS = 3;

function toFormValue(value: string | null | undefined): string {
  return value ?? "";
}

// Number of security-question pairs to show on open: the greater of the stored
// question / (masked) answer counts, capped at the max.
function initialSecurityCount(
  credential: OpsCredentialsMessage | null,
): number {
  if (!credential) {
    return 0;
  }
  const questions = credential.securityQuestions?.length ?? 0;
  const answers = credential.securityAnswers?.length ?? 0;
  return Math.min(Math.max(questions, answers), MAX_SECURITY_QUESTIONS);
}

let securityKeyCounter = 0;

function makeSecurityKey(): string {
  securityKeyCounter += 1;
  return `security-${securityKeyCounter}`;
}

interface OpsCredentialsFormProps {
  practices: PracticeMessage[];
  credential?: OpsCredentialsMessage | null;
  cancel: () => void;
  afterSubmit: () => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

export function OpsCredentialsForm({
  practices,
  credential = null,
  cancel,
  afterSubmit,
  updateSnackBar,
}: OpsCredentialsFormProps) {
  const queryClient = useQueryClient();
  const isEdit = !!credential;
  const defaultPracticeId =
    credential?.practiceId ??
    (practices.length === 1 ? practices[0].wieldyId : "");

  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    setValue,
    getValues,
  } = useForm<OpsCredentialsFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 1000,
    resolver: zodResolver(opsCredentialsFormSchema),
    defaultValues: {
      practiceId: defaultPracticeId,
      category:
        (credential?.category as OpsCredentialsFormFields["category"]) ?? "",
      owner: (credential?.owner as OpsCredentialsFormFields["owner"]) ?? "",
      status:
        (credential?.status as OpsCredentialsFormFields["status"]) ??
        OPS_STATUS.ACTIVE,
      name: toFormValue(credential?.name),
      targetName: toFormValue(credential?.targetName),
      website: toFormValue(credential?.website),
      username: toFormValue(credential?.username),
      password: toFormValue(credential?.password),
      accountHolderName: toFormValue(credential?.accountHolderName),
      accountHolderDob: toFormValue(credential?.accountHolderDob),
      mfaType:
        (credential?.mfaType as OpsCredentialsFormFields["mfaType"]) ??
        OPS_MFA_TYPE.NONE,
      mfaEmail: toFormValue(credential?.mfaEmail),
      mfaPhone: toFormValue(credential?.mfaPhone),
      totpSecret: toFormValue(credential?.totpSecret),
      securityQuestions: credential?.securityQuestions ?? [],
      securityAnswers: credential?.securityAnswers ?? [],
      notes: toFormValue(credential?.notes),
    },
  });

  const mfaType = useWatch({ control, name: "mfaType" });

  const [showAccountHolder, setShowAccountHolder] = useState(
    isEdit &&
      (!!credential?.accountHolderName || !!credential?.accountHolderDob),
  );
  const [securityKeys, setSecurityKeys] = useState<string[]>(() =>
    Array.from({ length: initialSecurityCount(credential) }, makeSecurityKey),
  );
  const [targetOptions, setTargetOptions] = useState<string[]>([]);

  useEffect(() => {
    let active = true;
    queryClient
      .getOpsTargetOptions()
      .then((options) => {
        if (active) {
          setTargetOptions(options);
        }
        return options;
      })
      .catch(() => {
        if (active) {
          setTargetOptions([]);
        }
      });
    return () => {
      active = false;
    };
  }, [queryClient]);

  const securityCount = securityKeys.length;

  const removeAccountHolder = () => {
    setValue("accountHolderName", "");
    setValue("accountHolderDob", "");
    setShowAccountHolder(false);
  };

  const removeSecurityQuestion = (index: number) => {
    const questions = [...(getValues("securityQuestions") ?? [])];
    const answers = [...(getValues("securityAnswers") ?? [])];
    questions.splice(index, 1);
    answers.splice(index, 1);
    setValue("securityQuestions", questions);
    setValue("securityAnswers", answers);
    setSecurityKeys((keys) => keys.filter((_, i) => i !== index));
  };

  const addSecurityQuestion = () => {
    setSecurityKeys((keys) => [...keys, makeSecurityKey()]);
  };

  const buildPayload = (
    data: OpsCredentialsFormFields,
  ): OpsCredentialPayload => {
    const payload: OpsCredentialPayload = {
      category: data.category,
      owner: data.owner || null,
      status: data.status,
      name: data.name,
      targetName: data.targetName,
      website: data.website,
      username: data.username,
      password: data.password,
      accountHolderName: data.accountHolderName,
      accountHolderDob: data.accountHolderDob,
      mfaType: data.mfaType,
      mfaEmail: data.mfaEmail,
      mfaPhone: data.mfaPhone,
      totpSecret: data.totpSecret,
      notes: data.notes,
    };
    // On edit only send fields the user can have changed: drop empty strings and
    // still-masked secret values so we never overwrite stored secrets with a mask.
    if (isEdit) {
      (Object.keys(payload) as (keyof OpsCredentialPayload)[]).forEach(
        (key) => {
          const value = payload[key];
          if (
            key !== "category" &&
            key !== "name" &&
            (isNil(value) || value === "" || value === MASK)
          ) {
            delete payload[key];
          }
        },
      );
    }

    // Trim empty pairs; keep MASK answers as-is so the backend preserves them.
    const questions = data.securityQuestions ?? [];
    const answers = data.securityAnswers ?? [];
    const trimmedQuestions: string[] = [];
    const trimmedAnswers: string[] = [];
    for (let i = 0; i < securityCount; i += 1) {
      const question = (questions[i] ?? "").trim();
      const answer = answers[i] ?? "";
      if (question || answer.trim()) {
        trimmedQuestions.push(question);
        trimmedAnswers.push(answer);
      }
    }
    payload.securityQuestions = trimmedQuestions;
    payload.securityAnswers = trimmedAnswers;

    return payload;
  };

  const onSubmit = async (data: OpsCredentialsFormFields) => {
    try {
      const payload = buildPayload(data);
      if (isEdit && credential) {
        await Promise.all([
          queryClient.patchOpsCredential(credential.wieldyId, payload),
          stall(),
        ]);
      } else {
        await Promise.all([
          queryClient.createOpsCredential(data.practiceId, payload),
          stall(),
        ]);
      }
      updateSnackBar({
        severity: "success",
        message: `Successfully ${isEdit ? "updated" : "saved"} credential.`,
      });
      afterSubmit();
    } catch (error) {
      updateSnackBar({
        severity: "error",
        message: "Failed to save credential. Try again.",
      });
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to save ops credential: ${error}`);
    }
  };

  const hasErrors = Object.keys(errors).length > 0;
  const isDisabled = isSubmitting || hasErrors || !isValid;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      <Typography
        sx={{ fontSize: "1.125rem", fontWeight: 600, color: "#303030" }}
      >
        {isEdit ? "Edit credential" : "Add a credential"}
      </Typography>

      <LineBreak />

      {isEdit ? (
        <FormControl sx={rowSx}>
          <FormLabel>
            <InputLabelText>Practice</InputLabelText>
          </FormLabel>
          <InputWrapper>
            <Typography sx={{ color: "#475467", padding: "12px 0" }}>
              {credential?.practiceName ||
                practices.find((p) => p.wieldyId === credential?.practiceId)
                  ?.displayName ||
                "—"}
            </Typography>
          </InputWrapper>
        </FormControl>
      ) : (
        <SelectRow
          control={control}
          name="practiceId"
          label="Practice"
          options={practices.map((p) => ({
            value: p.wieldyId,
            label: p.displayName,
          }))}
        />
      )}

      <SelectRow
        control={control}
        name="category"
        label="Category"
        options={CATEGORY_OPTIONS}
      />
      <SelectRow
        control={control}
        name="owner"
        label="Owner"
        options={OWNER_OPTIONS}
      />
      <SelectRow
        control={control}
        name="status"
        label="Status"
        options={STATUS_OPTIONS}
      />
      <TextRow
        control={control}
        name="name"
        label="Name"
        placeholder="Enter a name"
      />

      <Controller
        name="targetName"
        control={control}
        render={({
          field: { ref, value, onBlur, onChange },
          fieldState: { error },
        }) => (
          <FormControl sx={rowSx}>
            <FormLabel>
              <InputLabelText>Target</InputLabelText>
            </FormLabel>
            <InputWrapper>
              <Autocomplete
                freeSolo
                options={targetOptions}
                inputValue={value ?? ""}
                onInputChange={(_, newValue) => onChange(newValue)}
                onBlur={() => {
                  if (value) {
                    onChange(canonicalizeTarget(value, targetOptions));
                  }
                  onBlur();
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={ref}
                    variant="outlined"
                    placeholder="Payer / portal / system this login is for"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: "off",
                    }}
                    sx={inputSx}
                  />
                )}
              />
              <ErrorHelperText message={error?.message} />
            </InputWrapper>
          </FormControl>
        )}
      />

      <TextRow
        control={control}
        name="website"
        label="Website"
        placeholder="https://..."
      />

      <LineBreak />

      <TextRow control={control} name="username" label="Username" />
      <TextRow
        control={control}
        name="password"
        label="Password"
        type="password"
      />

      {showAccountHolder ? (
        <>
          <TextRow
            control={control}
            name="accountHolderName"
            label="Account holder name"
          />
          <TextRow
            control={control}
            name="accountHolderDob"
            label="Account holder DOB"
            placeholder="YYYY-MM-DD"
          />
          <Button
            variant="text"
            startIcon={<RemoveCircleOutlineIcon />}
            onClick={removeAccountHolder}
            sx={addButtonSx}
          >
            Remove account holder details
          </Button>
        </>
      ) : (
        <Button
          variant="text"
          startIcon={<AddOutlinedIcon />}
          onClick={() => setShowAccountHolder(true)}
          sx={addButtonSx}
        >
          Add account holder details
        </Button>
      )}

      <LineBreak />

      <SelectRow
        control={control}
        name="mfaType"
        label="MFA type"
        options={MFA_TYPE_OPTIONS}
      />
      {mfaType === OPS_MFA_TYPE.EMAIL && (
        <TextRow
          control={control}
          name="mfaEmail"
          label="MFA email"
          type="email"
        />
      )}
      {mfaType === OPS_MFA_TYPE.SMS && (
        <TextRow control={control} name="mfaPhone" label="MFA phone" />
      )}
      {mfaType === OPS_MFA_TYPE.AUTHENTICATOR && (
        <Controller
          name="totpSecret"
          control={control}
          render={({
            field: { ref, name, value, onBlur, onChange },
            fieldState: { error },
          }) => (
            <FormControl sx={sectionSx}>
              <FormLabel>
                <InputLabelText>Setup key</InputLabelText>
              </FormLabel>
              <InputWrapper>
                <AuthenticatorSetupKeyField
                  name={name}
                  value={value ?? ""}
                  inputRef={ref}
                  error={error?.message}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              </InputWrapper>
            </FormControl>
          )}
        />
      )}

      <FormControl sx={sectionSx}>
        <FormLabel>
          <InputLabelText>Security questions</InputLabelText>
        </FormLabel>
        <InputWrapper>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
            {securityKeys.map((key, index) => (
              <Box
                key={key}
                sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8125rem",
                      fontWeight: 600,
                      color: "#475467",
                    }}
                  >
                    Question {index + 1}
                  </Typography>
                  <IconButton
                    aria-label={`Remove question ${index + 1}`}
                    size="small"
                    onClick={() => removeSecurityQuestion(index)}
                    sx={{ color: "#475467" }}
                  >
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
                <BareTextField
                  control={control}
                  name={`securityQuestions.${index}`}
                  placeholder="Security question"
                />
                <BareTextField
                  control={control}
                  name={`securityAnswers.${index}`}
                  placeholder="Answer"
                />
              </Box>
            ))}
            {securityCount < MAX_SECURITY_QUESTIONS && (
              <Button
                variant="text"
                startIcon={<AddOutlinedIcon />}
                onClick={addSecurityQuestion}
                sx={addButtonSx}
              >
                Add security question
              </Button>
            )}
          </Box>
        </InputWrapper>
      </FormControl>

      <TextRow
        control={control}
        name="notes"
        label="Notes"
        multiline
        rows={3}
      />

      <LineBreak />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          marginLeft: "auto",
          gap: "12px",
        }}
      >
        <Button
          sx={{
            backgroundColor: "#EAECF0",
            color: "#475467",
            borderRadius: "8px",
            "&:hover": { backgroundColor: "#e6e7ea" },
          }}
          onClick={cancel}
          type="button"
          variant="contained"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          sx={{ borderRadius: "8px" }}
          type="submit"
          color="primary"
          variant="contained"
          disabled={isDisabled}
          data-testid="submitOpsCredentials"
        >
          {isSubmitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <CircularProgress size={20} />
              Submitting...
            </Box>
          ) : (
            "Save"
          )}
        </Button>
      </Box>
    </Box>
  );
}

OpsCredentialsForm.defaultProps = {
  credential: null,
};
